import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Users as UsersIcon, CalendarCheck2, DollarSign } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, Legend,
} from "recharts";
import { api } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

const TEAL = "rgb(var(--mb-primary))";
const STATUS_COLORS = { confirmed: "#14b8a6", completed: "#71717a", cancelled: "#52525b", no_show: "#ef4444", pending_payment: "#eab308" };

export default function AdminOverview() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-sm text-mb-fg2">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ overview</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Platform health</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Hospitals" value={`${data.hospitals.active} / ${data.hospitals.total}`} sub={`${data.hospitals.pending} pending`} icon={<Building2 className="h-4 w-4" />} testid="kpi-hospitals" />
        <Stat label="Users" value={data.users.total} sub={`${data.users.doctors} doctors · ${data.users.patients} patients`} icon={<UsersIcon className="h-4 w-4" />} testid="kpi-users" />
        <Stat label="Appointments" value={data.appointments.total} sub="all time" icon={<CalendarCheck2 className="h-4 w-4" />} testid="kpi-appointments" />
        <Stat label="Net revenue" value={formatCurrency(data.revenue.net)} sub={`gross ${formatCurrency(data.revenue.gross)} · refunded ${formatCurrency(data.revenue.refunded)}`} icon={<DollarSign className="h-4 w-4" />} testid="kpi-revenue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="rounded-xl border border-mb-border bg-mb-surface p-5" data-testid="chart-status">
          <div className="text-sm font-medium mb-4">Appointments by status</div>
          {data.appointments.by_status.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-zinc-600">No data</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.appointments.by_status} dataKey="count" nameKey="label" innerRadius={60} outerRadius={90} paddingAngle={3}>
                    {data.appointments.by_status.map((s) => <Cell key={s.label} fill={STATUS_COLORS[s.label] || "#6b7280"} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "rgb(var(--mb-surface2))", border: "1px solid rgb(var(--mb-border))", color: "rgb(var(--mb-fg))" }} />
                  <Legend wrapperStyle={{ color: "#a1a1aa", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-mb-border bg-mb-surface p-5" data-testid="chart-top-hospitals">
          <div className="text-sm font-medium mb-4">Top 5 hospitals · gross revenue</div>
          {data.top_hospitals.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-zinc-600">No data</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={data.top_hospitals} layout="vertical" margin={{ left: 80, right: 16 }}>
                  <CartesianGrid stroke="rgb(var(--mb-border))" horizontal={false} />
                  <XAxis type="number" stroke="#71717a" fontSize={11} />
                  <YAxis dataKey="name" type="category" stroke="#71717a" fontSize={11} width={120} />
                  <Tooltip contentStyle={{ background: "rgb(var(--mb-surface2))", border: "1px solid rgb(var(--mb-border))", color: "rgb(var(--mb-fg))" }} />
                  <Bar dataKey="revenue" fill={TEAL} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>

      <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid="top-hospitals-table">
        <div className="px-6 py-4 border-b border-mb-border text-sm font-medium">Top hospitals</div>
        <ul className="divide-y divide-mb-border">
          {data.top_hospitals.map((h) => (
            <li key={h.hospital_id} className="px-5 py-3 flex items-center gap-4">
              <Link to={`/admin/hospitals/${h.hospital_id}`} className="flex-1 text-sm text-white truncate hover:text-teal-300">{h.name}</Link>
              <div className="text-sm text-teal-300 tabular-nums">{formatCurrency(h.revenue, { decimals: true })}</div>
            </li>
          ))}
          {data.top_hospitals.length === 0 && <li className="px-6 py-8 text-sm text-mb-fg2 text-center">No revenue yet.</li>}
        </ul>
      </section>
    </div>
  );
}

function Stat({ label, value, sub, icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-mb-border bg-mb-surface p-5">
      <div className="flex items-center justify-between text-mb-fg2">
        <span className="text-[10px] uppercase tracking-[0.25em]">{label}</span>
        <span className="text-teal-400">{icon}</span>
      </div>
      <div className="mt-3 text-3xl font-light text-white">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-mb-fg2 truncate">{sub}</div>}
    </div>
  );
}
